import './friction-score.css';

/** Lower bounds of the reading levels of a friction score, shared by every screen. */
const WATCH_FROM = 40;
const HARD_FROM = 70;

/** From this share of exits on a page, the figure is shown as an alert. */
export const NOTABLE_EXIT_RATE = 0.4;

export type FrictionLevel = 'low' | 'watch' | 'hard';

export const FRICTION_LEVEL_LABELS: Record<FrictionLevel, string> = {
  low: 'Faible',
  watch: 'À surveiller',
  hard: 'Difficile',
};

export function frictionLevel(score: number): FrictionLevel {
  if (score >= HARD_FROM) return 'hard';
  if (score >= WATCH_FROM) return 'watch';
  return 'low';
}

/** The level ranges as the legend writes them. */
export const FRICTION_LEVEL_RANGES: Record<FrictionLevel, string> = {
  low: `0 à ${WATCH_FROM - 1}`,
  watch: `${WATCH_FROM} à ${HARD_FROM - 1}`,
  hard: `${HARD_FROM} et plus`,
};

interface FrictionScoreProps {
  score: number;
  /** A thin bar under the figure, for the places that have the room. */
  bar?: boolean;
}

/**
 * A friction score, read the same way everywhere: the figure out of 100 and its
 * level in words, coloured by level — the words carry the meaning, the colour
 * only repeats it.
 */
export function FrictionScore({ score, bar = false }: FrictionScoreProps) {
  const level = frictionLevel(score);
  return (
    <span className={`friction friction--${level}${bar ? ' friction--with-bar' : ''}`}>
      <span className="friction__line">
        <span className="friction__value">
          {score}
          <span className="friction__max">/100</span>
        </span>
        <span className="friction__level">{FRICTION_LEVEL_LABELS[level]}</span>
      </span>
      {bar && (
        <span className="friction__track" aria-hidden="true">
          <span className="friction__fill" style={{ width: `${Math.max(0, Math.min(100, score))}%` }} />
        </span>
      )}
    </span>
  );
}
